import React, { useEffect } from 'react';

import { Box, CircularProgress, Container, makeStyles } from '@material-ui/core';
import { useAnalytics, useBackRouter } from 'hooks';
import { GetStaticPaths, GetStaticPropsContext, InferGetStaticPropsType, NextPage } from 'next';
import { NextSeo } from 'next-seo';
import { QueryKeys, useFavorite, useGetGameDetails } from 'queries';
import { useIntl } from 'react-intl';
import { dehydrate, QueryClient } from 'react-query';
import { Routes } from 'routes';

import { enhancePathsWithLocales, generatePath } from 'utils/routes.utils';

import { Duration } from 'constants/cache';
import { Events } from 'constants/events';
import apiService from 'services/api/ApiService';
import { CollectionInstance } from 'services/api/definitions/collections.definitions';
import {
  ContentType,
  Game,
  GameSubscription,
  SubscriptionType,
} from 'services/api/definitions/game.definitions';

import Background from 'components/@common/Background';
import DetailsNavigationBar from 'components/@game-detail/DetailsNavigationBar/DetailsNavigationBar';
import GameDetail from 'components/@game-detail/GameDetail';
import SubscriptionDetail from 'components/@game-detail/SubscriptionDetail';
import TitleBar from 'components/@game-detail/TitleBar/TitleBar';

const useStyles = makeStyles({
  offset: { marginTop: 56 },
  playButtonSub: { width: '100%', textAlign: 'center' },
});

const GameDetails: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  slug = '',
  gameId = '',
  collectionId = '',
}) => {
  const router = useBackRouter(Routes.home);
  const { game } = useGetGameDetails(gameId);
  const { isFavoritedGame, favoriteGame, unFavoriteGame } = useFavorite();
  const { send } = useAnalytics();

  const path = !!slug.length ? generatePath(Routes.game, { slug }) : '';

  const intl = useIntl();
  const classes = useStyles();

  useEffect(() => {
    if (!!path.length) {
      router.prefetch(path);
    }
  }, [router, path]);

  useEffect(() => {
    send({
      event: Events.gameSeen,
      data: { game: { slug: game?.slugName } },
      withUser: true,
    });
  }, [send, game?.slugName]);

  if (router.isFallback || !game) {
    return (
      <Box height="100%" display="flex" alignItems="center" justifyContent="center">
        <CircularProgress size={24} />
      </Box>
    );
  }

  const handleFavorite = (isFavorited: boolean) => {
    if (isFavorited) {
      favoriteGame({ id: game.id });
    } else {
      unFavoriteGame({ id: game.id });
    }
  };

  const isSubscription = game.contentType === ContentType.subscription;

  return (
    <>
      <NextSeo
        title={intl.formatMessage({ id: 'seo.title.games.slug' }, { game: game.name })}
        description={game.description}
        openGraph={{ images: [{ url: game.largeTeaserImage }] }}
        additionalMetaTags={[
          {
            // Required because we use digi api to unsubscribe which is http
            httpEquiv: 'content-security-policy',
            content: 'upgrade-insecure-requests',
          },
        ]}
      />
      <Background />
      <DetailsNavigationBar
        title={game.name}
        category={game.collection}
        iconUrl={game.smallTeaserImage}
        onBackClick={router.back}
        price={game.price}
        payType={intl.formatMessage({
          id:
            game.subscriptionType === SubscriptionType.oneTimePurchase
              ? 'game.explainer_text.one_time_pay'
              : 'game.explainer_text.per_game_session',
        })}
      />
      <div className={classes.offset} />
      <Container maxWidth="sm" disableGutters>
        <main>
          <TitleBar
            title={game.name}
            category={game.collection}
            iconUrl={game.smallTeaserImage}
            isFavorited={isFavoritedGame(game.id)}
            bannerImage={game.largeTeaserImage}
            onFavoriteClick={handleFavorite}
          />
          {isSubscription ? (
            <SubscriptionDetail subscription={game} collectionId={collectionId} />
          ) : (
            <GameDetail game={game} collectionId={collectionId} />
          )}
        </main>
      </Container>
    </>
  );
};

export default GameDetails;

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const games = await apiService.getGameSlugs();
  const paths = games.map(game => ({ params: { slug: game.slugName } }));

  const finalPaths = enhancePathsWithLocales(paths, locales);

  return { paths: finalPaths, fallback: true };
};

export const getStaticProps = async ({ params }: GetStaticPropsContext<{ slug: string }>) => {
  const queryClient = new QueryClient();

  let data = {
    gameId: '',
    collectionId: '',
    slug: params?.slug ?? '',
  };

  try {
    const games = await apiService.getGameSlugs();
    const gameId = games.find(g => g.slugName === params?.slug)?.id;

    let game: Game | GameSubscription | undefined;
    let collection: CollectionInstance | undefined;

    if (gameId) {
      data.gameId = gameId;

      game = await apiService.getGameDetails(gameId);

      if (game) {
        const collections = await apiService.getCollections();

        //TODO: we check based on name, which is okay for now, game should also have the collection id or slugName to make it more robust
        collection = collections.find(collection => collection.name === game?.collection);

        data.collectionId = collection?.id ?? '';

        await queryClient.prefetchInfiniteQuery(
          QueryKeys.collectionbyid.all(collection?.id ?? ''),
          () => apiService.getGameCollection(collection?.id ?? ''),
        );
      }

      await queryClient.prefetchQuery(QueryKeys.gamedetail.byId(gameId), () =>
        apiService.getGameDetails(gameId),
      );
    }
    if (!game) {
      return { notFound: true, revalidate: Duration.ONE_HOUR };
    }
  } catch (err) {
    return { notFound: true, revalidate: Duration.ONE_HOUR };
  }

  return {
    props: {
      // https://github.com/tannerlinsley/react-query/issues/1458
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
      ...data,
    },
    revalidate: Duration.ONE_HOUR,
  };
};
